import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ThemeManifest, ThemeRegistry } from '@classy/theme-sdk';
import { useAuth } from '../../auth/index.js';
import { useApi } from '../../api/ApiProvider.js';
import { ApiError } from '../../api/client.js';
import type { AdminOrganizationDto } from '../../api/admin.js';
import { SCHOOL_SPECIFIC_THEME_KEYS } from '../../theme/themeAccess.js';
import styles from './AdminPage.module.css';

const ORGS_QUERY_KEY = ['admin', 'organizations'] as const;

type AdminTab = 'general' | 'themes';

export interface AdminPageProps {
  themeRegistry: ThemeRegistry;
}

/**
 * Sarah's school-settings console. It mirrors the teacher flow:
 * pick a school first, then manage that school's settings on a familiar
 * tabbed settings page.
 */
export function AdminPage({ themeRegistry }: AdminPageProps) {
  const auth = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('general');
  const [schoolSearch, setSchoolSearch] = useState('');

  const orgsQuery = useQuery({
    queryKey: ORGS_QUERY_KEY,
    queryFn: () => api.admin.listOrganizations(),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 403) return false;
      return failureCount < 2;
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.admin.approveOrganization(id),
    onSuccess: (updated) => {
      setSelectedOrgId(updated.id);
      setActiveTab('general');
      queryClient.setQueryData<AdminOrganizationDto[]>(ORGS_QUERY_KEY, (previous) => {
        if (!previous) return [updated];
        return previous.map((org) => (org.id === updated.id ? updated : org));
      });
      queryClient.invalidateQueries({ queryKey: ORGS_QUERY_KEY });
    },
  });

  const themeMutation = useMutation({
    mutationFn: (input: { id: string; enabledThemeKeys: string[] }) =>
      api.admin.updateOrganizationThemes(input.id, input.enabledThemeKeys),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ORGS_QUERY_KEY });
      const previous = queryClient.getQueryData<AdminOrganizationDto[]>(ORGS_QUERY_KEY);
      if (previous) {
        queryClient.setQueryData<AdminOrganizationDto[]>(
          ORGS_QUERY_KEY,
          previous.map((org) =>
            org.id === input.id ? { ...org, enabledThemeKeys: input.enabledThemeKeys } : org,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(ORGS_QUERY_KEY, ctx.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ORGS_QUERY_KEY });
    },
  });

  const orgs = orgsQuery.data ?? [];
  const selectedOrg = selectedOrgId ? (orgs.find((org) => org.id === selectedOrgId) ?? null) : null;

  useEffect(() => {
    if (selectedOrgId && orgsQuery.isSuccess && !selectedOrg) setSelectedOrgId(null);
  }, [selectedOrg, selectedOrgId, orgsQuery.isSuccess]);

  const pending = orgs.filter((o) => o.status === 'pending');
  const approved = orgs.filter((o) => o.status === 'approved');
  const blocked = orgs.filter((o) => o.status === 'blocked');
  const themeCatalog = useMemo(() => sortThemes(themeRegistry.list()), [themeRegistry]);

  const toggleTheme = (org: AdminOrganizationDto, key: string) => {
    const next = new Set(org.enabledThemeKeys);
    if (next.has(key)) {
      if (next.size === 1) return;
      next.delete(key);
    } else {
      next.add(key);
    }
    themeMutation.mutate({ id: org.id, enabledThemeKeys: [...next].sort() });
  };

  if (auth.status === 'unauthenticated' || auth.status === 'loading') {
    return (
      <div className={styles.shell}>
        <h1 className={styles.heading}>Classy admin</h1>
        <p className={styles.bodyText}>Sign in to continue.</p>
      </div>
    );
  }

  if (orgsQuery.isLoading) {
    return (
      <div className={styles.shell}>
        <h1 className={styles.heading}>Classy admin</h1>
        <p className={styles.bodyText}>Loading schools...</p>
      </div>
    );
  }

  if (orgsQuery.isError) {
    const err = orgsQuery.error;
    const notAdmin = err instanceof ApiError && err.status === 403;
    return (
      <div className={styles.shell}>
        <h1 className={styles.heading}>Classy admin</h1>
        <p className={styles.bodyText}>
          {notAdmin
            ? 'Your account is not authorized to approve schools.'
            : 'Could not load schools.'}
        </p>
        <button type="button" className="glass-pill" onClick={() => auth.signOut()}>
          Sign out
        </button>
      </div>
    );
  }

  if (selectedOrg) {
    return (
      <SchoolSettings
        org={selectedOrg}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onBack={() => setSelectedOrgId(null)}
        onSignOut={() => auth.signOut()}
        themeCatalog={themeCatalog}
        themeRegistry={themeRegistry}
        onToggleTheme={(key) => toggleTheme(selectedOrg, key)}
        togglePending={themeMutation.isPending && themeMutation.variables?.id === selectedOrg.id}
        toggleError={
          themeMutation.isError && themeMutation.variables?.id === selectedOrg.id
            ? themeMutation.error
            : null
        }
      />
    );
  }

  return (
    <SchoolChooser
      signedInName={auth.account?.name ?? auth.account?.email ?? 'admin'}
      approved={approved}
      pending={pending}
      blocked={blocked}
      search={schoolSearch}
      onSearchChange={setSchoolSearch}
      onSelectSchool={(id) => {
        setSelectedOrgId(id);
        setActiveTab('general');
      }}
      onSignOut={() => auth.signOut()}
      onApprove={(id) => approveMutation.mutate(id)}
      approvePendingId={approveMutation.isPending ? approveMutation.variables : null}
      approveErrorId={approveMutation.isError ? (approveMutation.variables ?? null) : null}
      approveError={approveMutation.error}
    />
  );
}

function SchoolChooser({
  signedInName,
  approved,
  pending,
  blocked,
  search,
  onSearchChange,
  onSelectSchool,
  onSignOut,
  onApprove,
  approvePendingId,
  approveErrorId,
  approveError,
}: {
  signedInName: string;
  approved: AdminOrganizationDto[];
  pending: AdminOrganizationDto[];
  blocked: AdminOrganizationDto[];
  search: string;
  onSearchChange: (value: string) => void;
  onSelectSchool: (id: string) => void;
  onSignOut: () => void;
  onApprove: (id: string) => void;
  approvePendingId: string | null;
  approveErrorId: string | null;
  approveError: Error | null;
}) {
  const normalizedSearch = search.trim().toLowerCase();
  const visibleApproved = normalizedSearch
    ? approved.filter(
        (org) =>
          org.displayName.toLowerCase().includes(normalizedSearch) ||
          org.entraTenantId.toLowerCase().includes(normalizedSearch),
      )
    : approved;

  return (
    <div className={styles.shell} data-testid="admin-page">
      <header className={styles.header}>
        <h1 className={styles.heading}>Classy admin</h1>
        <p className={styles.subtitle}>
          Signed in as <strong>{signedInName}</strong>
        </p>
      </header>

      <nav className={`glass ${styles.tabs}`} role="tablist" aria-label="Admin sections">
        <button
          type="button"
          role="tab"
          aria-selected="true"
          className={`${styles.tab} ${styles.tabActive}`}
        >
          Schools
        </button>
        <button type="button" className={styles.logout} onClick={onSignOut}>
          Sign out
        </button>
      </nav>

      <section className={styles.body}>
        <div className={styles.schoolToolbar}>
          <div>
            <h2 className={styles.sectionTitle}>Approved schools</h2>
            <p className={styles.sectionHint}>Choose a school to manage its settings.</p>
          </div>
          <label className={styles.searchLabel}>
            <span>Search schools</span>
            <input
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              className={styles.searchInput}
              type="search"
              placeholder="School name"
            />
          </label>
        </div>

        {approved.length === 0 ? (
          <div className={`glass glass--solid ${styles.empty}`}>
            <p>No approved schools yet.</p>
          </div>
        ) : null}

        {approved.length > 0 && visibleApproved.length === 0 ? (
          <div className={`glass glass--solid ${styles.empty}`}>
            <p>No approved schools match that search.</p>
          </div>
        ) : null}

        {visibleApproved.length > 0 ? (
          <ul className={styles.schoolGrid} aria-label="Approved schools">
            {visibleApproved.map((org) => (
              <li key={org.id}>
                <SchoolCard org={org} onOpen={() => onSelectSchool(org.id)} />
              </li>
            ))}
          </ul>
        ) : null}

        {pending.length > 0 ? (
          <section className={styles.groupSection} aria-label="Pending schools">
            <div className={styles.groupHead}>
              <h2 className={styles.sectionTitle}>Pending approval</h2>
              <span className={styles.count}>{pending.length}</span>
            </div>
            <ul className={styles.schoolGrid}>
              {pending.map((org) => (
                <li key={org.id}>
                  <SchoolCard
                    org={org}
                    onApprove={() => onApprove(org.id)}
                    approvePending={approvePendingId === org.id}
                    approveError={approveErrorId === org.id ? approveError : null}
                  />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {blocked.length > 0 ? (
          <section className={styles.groupSection} aria-label="Blocked schools">
            <div className={styles.groupHead}>
              <h2 className={styles.sectionTitle}>Blocked</h2>
              <span className={styles.count}>{blocked.length}</span>
            </div>
            <ul className={styles.schoolGrid}>
              {blocked.map((org) => (
                <li key={org.id}>
                  <SchoolCard org={org} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </section>
    </div>
  );
}

function SchoolCard({
  org,
  onOpen,
  onApprove,
  approvePending = false,
  approveError = null,
}: {
  org: AdminOrganizationDto;
  onOpen?: () => void;
  onApprove?: () => void;
  approvePending?: boolean;
  approveError?: Error | null;
}) {
  const canOpen = !!onOpen;

  return (
    <article className={`glass glass--solid ${styles.schoolCard}`}>
      <button
        type="button"
        className={styles.schoolCardButton}
        onClick={onOpen}
        disabled={!canOpen}
        aria-label={canOpen ? `Open ${org.displayName}` : org.displayName}
        data-testid={`admin-school-${org.entraTenantId}`}
      >
        <span className={styles.schoolCardText}>
          <span className={styles.schoolName}>{org.displayName}</span>
          <span className={styles.schoolMeta}>
            {org.usage.teacherCount} user{org.usage.teacherCount === 1 ? '' : 's'},{' '}
            {org.usage.classroomCount} classroom{org.usage.classroomCount === 1 ? '' : 's'}
          </span>
          <span className={styles.tenantId}>{org.entraTenantId}</span>
          {approveError ? (
            <span className={styles.errorLine}>Approve failed: {approveError.message}</span>
          ) : null}
        </span>
        <span className={styles.schoolStats} aria-hidden="true">
          <span className={styles.schoolStat}>
            <strong>{org.usage.activeTeacherCount}</strong>
            <span>active users</span>
          </span>
          <span className={styles.schoolStat}>
            <strong>{org.usage.activeClassroomCount}</strong>
            <span>active rooms</span>
          </span>
        </span>
      </button>
      <footer className={styles.schoolFooter}>
        {onApprove && org.status === 'pending' ? (
          <button
            type="button"
            className={styles.approve}
            onClick={onApprove}
            disabled={approvePending}
            data-testid={`approve-${org.entraTenantId}`}
          >
            {approvePending ? 'Approving...' : 'Approve'}
          </button>
        ) : (
          <span className={statusClass(org.status)}>{statusLabel(org)}</span>
        )}
      </footer>
    </article>
  );
}

function SchoolSettings({
  org,
  activeTab,
  onTabChange,
  onBack,
  onSignOut,
  themeCatalog,
  themeRegistry,
  onToggleTheme,
  togglePending,
  toggleError,
}: {
  org: AdminOrganizationDto;
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  onBack: () => void;
  onSignOut: () => void;
  themeCatalog: ThemeManifest[];
  themeRegistry: ThemeRegistry;
  onToggleTheme: (key: string) => void;
  togglePending: boolean;
  toggleError: Error | null;
}) {
  return (
    <div className={styles.detail} data-testid="admin-school-detail">
      <section className={styles.header}>
        <h1 className={styles.pageHeading}>School Settings</h1>
        <h2 className={styles.title}>{org.displayName}</h2>
        <p className={styles.meta}>{org.entraTenantId}</p>
      </section>

      <header className={styles.topbar}>
        <button
          type="button"
          className={`glass-pill ${styles.back}`}
          onClick={onBack}
          aria-label="Back to schools"
        >
          <BackArrow />
          <span>Schools</span>
        </button>

        <div className={styles.actions}>
          <span className={statusClass(org.status)}>{statusLabel(org)}</span>
          <button
            type="button"
            className={`glass-pill ${styles.secondaryPill}`}
            onClick={onSignOut}
          >
            Sign out
          </button>
        </div>
      </header>

      <nav className={`glass ${styles.tabs}`} role="tablist" aria-label="School settings">
        <AdminTabButton tab="general" current={activeTab} onClick={() => onTabChange('general')}>
          General
        </AdminTabButton>
        <AdminTabButton tab="themes" current={activeTab} onClick={() => onTabChange('themes')}>
          Themes
        </AdminTabButton>
      </nav>

      <section className={styles.body}>
        {activeTab === 'general' ? (
          <div className={`glass glass--solid ${styles.panel}`}>
            <SchoolGeneralPanel org={org} />
          </div>
        ) : null}
        {activeTab === 'themes' ? (
          <div className={`glass glass--solid ${styles.panel}`}>
            <SchoolThemesPanel
              org={org}
              themeCatalog={themeCatalog}
              themeRegistry={themeRegistry}
              onToggleTheme={onToggleTheme}
              togglePending={togglePending}
              toggleError={toggleError}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}

function AdminTabButton({
  tab,
  current,
  onClick,
  children,
}: {
  tab: AdminTab;
  current: AdminTab;
  onClick: () => void;
  children: string;
}) {
  const selected = tab === current;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      className={selected ? `${styles.tab} ${styles.tabActive}` : styles.tab}
      onClick={onClick}
      data-testid={`admin-tab-${tab}`}
    >
      {children}
    </button>
  );
}

function SchoolGeneralPanel({ org }: { org: AdminOrganizationDto }) {
  return (
    <div className={styles.summary}>
      <section className={styles.summarySection}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Usage</h2>
        </div>
        <div className={styles.statsGrid} aria-label="School usage">
          <Stat label="Users" value={org.usage.teacherCount.toLocaleString()} />
          <Stat label="Active users" value={org.usage.activeTeacherCount.toLocaleString()} />
          <Stat label="Classrooms" value={org.usage.classroomCount.toLocaleString()} />
          <Stat label="Active rooms" value={org.usage.activeClassroomCount.toLocaleString()} />
        </div>
      </section>

      <section className={styles.summarySection}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>General</h2>
        </div>
        <div className={styles.infoGrid}>
          <InfoItem label="School" value={org.displayName} />
          <InfoItem label="Status" value={statusLabel(org)} />
          <InfoItem
            label="Last sign-in"
            value={org.usage.lastLoginAt ? formatDate(org.usage.lastLoginAt) : 'None'}
          />
          <InfoItem label="Tenant ID" value={org.entraTenantId} mono />
        </div>
      </section>
    </div>
  );
}

function SchoolThemesPanel({
  org,
  themeCatalog,
  themeRegistry,
  onToggleTheme,
  togglePending,
  toggleError,
}: {
  org: AdminOrganizationDto;
  themeCatalog: ThemeManifest[];
  themeRegistry: ThemeRegistry;
  onToggleTheme: (key: string) => void;
  togglePending: boolean;
  toggleError: Error | null;
}) {
  return (
    <div className={styles.summary}>
      <section className={styles.summarySection}>
        <div className={styles.sectionHead}>
          <div>
            <h2 className={styles.sectionTitle}>Themes</h2>
            <p className={styles.sectionHint}>
              Choose which themes teachers in this school can use.
            </p>
          </div>
          <span className={styles.count}>
            {org.enabledThemeKeys.length} of {themeCatalog.length}
          </span>
        </div>

        {toggleError ? (
          <p className={styles.errorLine} role="alert">
            Theme update failed: {toggleError.message}
          </p>
        ) : null}

        <div className={styles.themeGrid} aria-label="School themes">
          {themeCatalog.map((theme) => (
            <ThemeToggleTile
              key={theme.key}
              theme={theme}
              registry={themeRegistry}
              enabled={org.enabledThemeKeys.includes(theme.key)}
              disabled={togglePending}
              removingLast={
                org.enabledThemeKeys.length === 1 && org.enabledThemeKeys.includes(theme.key)
              }
              onToggle={() => onToggleTheme(theme.key)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function ThemeToggleTile({
  theme,
  registry,
  enabled,
  disabled,
  removingLast,
  onToggle,
}: {
  theme: ThemeManifest;
  registry: ThemeRegistry;
  enabled: boolean;
  disabled: boolean;
  removingLast: boolean;
  onToggle: () => void;
}) {
  const pack = registry.get(theme.key);
  const bg = pack?.getBackground('home');
  const mascot = pack?.getMascot('home');
  const isSchoolTheme = SCHOOL_SPECIFIC_THEME_KEYS.has(theme.key);

  return (
    <button
      type="button"
      className={enabled ? `${styles.themeTile} ${styles.themeTileEnabled}` : styles.themeTile}
      aria-pressed={enabled}
      disabled={disabled || removingLast}
      onClick={onToggle}
      data-testid={`admin-theme-${theme.key}`}
    >
      <span
        className={styles.themePreview}
        style={bg ? { backgroundImage: `url(${bg})` } : undefined}
        aria-hidden="true"
      >
        {mascot ? (
          <img className={styles.themeMascot} src={mascot} alt="" draggable={false} />
        ) : null}
      </span>
      <span className={styles.themeMeta}>
        <span className={styles.themeName}>{theme.name}</span>
        {isSchoolTheme ? <span className={styles.themeTag}>School</span> : null}
      </span>
      <span className={enabled ? styles.themeStateOn : styles.themeStateOff}>
        {enabled ? 'Enabled' : 'Off'}
      </span>
    </button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

function InfoItem({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className={styles.infoItem}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={mono ? `${styles.infoValue} ${styles.monoValue}` : styles.infoValue}>
        {value}
      </span>
    </div>
  );
}

function BackArrow() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function sortThemes(themes: ThemeManifest[]): ThemeManifest[] {
  return [...themes].sort((a, b) => {
    const aSchool = SCHOOL_SPECIFIC_THEME_KEYS.has(a.key) ? 1 : 0;
    const bSchool = SCHOOL_SPECIFIC_THEME_KEYS.has(b.key) ? 1 : 0;
    if (aSchool !== bSchool) return aSchool - bSchool;
    return a.name.localeCompare(b.name);
  });
}

function statusLabel(org: AdminOrganizationDto): string {
  if (org.status === 'approved') {
    return org.approvedAt ? `Approved ${formatDate(org.approvedAt)}` : 'Approved';
  }
  if (org.status === 'blocked') return 'Blocked';
  return 'Pending';
}

function statusClass(status: AdminOrganizationDto['status']): string {
  if (status === 'approved') return styles.statusApproved!;
  if (status === 'blocked') return styles.statusBlocked!;
  return styles.statusPending!;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
